import { useTranslation } from 'react-i18next'
import { DirectorYearlyTrajectoryValues } from 'core/api/trajectory-photo/useApiGetDirectorYearlyTrajectoriesGmv'
import { useTrajectoryAnalytics } from './analytics/use-trajectory-analytics'
import { NavLink } from 'react-router'
import { useFormatAmount } from 'core/hooks/useFormatAmount'
import { useFormatNumber } from 'core/hooks/useFormatNumber'
import { getPathFromRouteId } from 'core/utils/route-utils'
import { RouteId } from 'core/routing/router/route-ids'
import { TRAJECTORY_EVENTS } from 'core/constants/trajectory-analytics'
import { roundProgressionForDisplay } from '../../../core/utils/round-progression-for-display'
import StoreTrajectoryValidator from './store-trajectory-validator'

type StoreTrajectoryGmvCardProps = {
  yearlyValues: DirectorYearlyTrajectoryValues
  storeId: number
  currency: string
}

const StoreTrajectoryGmvCard = ({ yearlyValues, storeId, currency }: StoreTrajectoryGmvCardProps) => {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const trackTrajectoryEvent = useTrajectoryAnalytics()
  const formatAmount = useFormatAmount()
  const formatNumber = useFormatNumber()
  // List API progression is a percent ratio (0.123 = 12.3%); round at 1 percentage decimal.
  const displayProgressionRatio = roundProgressionForDisplay(yearlyValues.progression)

  return (
    <NavLink
      to={{
        pathname: getPathFromRouteId(
          yearlyValues.validatorUid ? RouteId.StoreTrajectoriesConsult : RouteId.StoreTrajectoriesEdit
        ),
        search: `?storeId=${storeId}&year=${yearlyValues.year}`
      }}
      onClick={() => {
        trackTrajectoryEvent(TRAJECTORY_EVENTS.TRAJECTORY_CLICKED, { year: yearlyValues.year, storeId })
      }}
      data-testid="store-trajectory-gmv-card"
      className="mg-store-trajectory-gmv-card"
      end
    >
      <div className="mg-store-trajectory-gmv-card-header vp-title-m">
        <div className="mg-store-trajectory-gmv-card-header-title vp-title-m">{yearlyValues.year}</div>
        <StoreTrajectoryValidator
          validatorUid={yearlyValues.validatorUid}
          validationDate={yearlyValues.validationDate}
        />
      </div>
      <div className="mg-store-trajectory-gmv-card-body vp-body-s">
        <div className="mg-store-trajectory-gmv-card-body-label vp-body-s">
          {t('general.trajectory.store.instore-gmv-trajectory')}
        </div>
        <div className="mg-store-trajectory-gmv-card-body-value vp-subtitle-l">
          {formatAmount(yearlyValues.trajectoryGmvValue, currency, '-')}
          <span
            className={`mg-store-trajectory-gmv-card-body-value-progression${displayProgressionRatio >= 0 ? '-positive' : '-negative'} vp-body-m`}
          >
            {formatNumber(displayProgressionRatio, '', {
              style: 'percent',
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
              signDisplay: 'exceptZero'
            })}
          </span>
          {displayProgressionRatio !== 0 && yearlyValues.year > currentYear && <span className="vp-body-m">*</span>}
        </div>
      </div>
    </NavLink>
  )
}

export default StoreTrajectoryGmvCard
